import React, { useMemo } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User } from "lucide-react";

interface InstrumentAvatarProps {
  instrumentKey: string;
  name?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

/**
 * Generates a deterministic color based on a string
 * @param str The input string (instrumentKey)
 * @returns A HSL color string
 */
const generateColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  // Generate HSL color with good saturation and lightness
  // Using a higher lightness value to ensure text readability
  const h = hash % 360;
  const s = 65 + (hash % 20); // 65-85% saturation
  const l = 65 + (hash % 15); // 65-80% lightness - lighter to ensure text contrast

  return `hsl(${h}, ${s}%, ${l}%)`;
};

/**
 * Gets initials from a name (up to 2 characters)
 */
const getInitials = (name: string): string => {
  if (!name) return "";

  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }

  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

export const InstrumentAvatar: React.FC<InstrumentAvatarProps> = ({
  instrumentKey: instrumentKey,
  name,
  className = "",
  size = "md",
}) => {
  const bgColor = useMemo(() => generateColor(instrumentKey), [instrumentKey]);

  const initials = useMemo(() => {
    if (name) return getInitials(name);

    // Use the instrumentKey if no name is provided
    return instrumentKey.substring(0, 2).toUpperCase();
  }, [name, instrumentKey]);

  const sizeClasses = {
    sm: "h-6 w-6 text-xs",
    md: "h-8 w-8 text-sm",
    lg: "h-10 w-10 text-base",
  };

  // Determine text color based on background brightness
  const textColor = useMemo(() => {
    // Always use dark text for better readability with our lighter backgrounds
    return "text-gray-900";
  }, []);

  return (
    <Avatar className={`rounded-full ${className}`}>
      <AvatarFallback
        className={`${sizeClasses[size]} ${textColor} rounded-full`}
        style={{ backgroundColor: bgColor }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

export default InstrumentAvatar;
